<script setup lang="ts">
import type { EchartsUIType } from '@vben/plugins/echarts';

import type { ChannelKey, ProductColumnDefinition } from './analytics-metrics';

import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { EchartsUI, useEcharts } from '@vben/plugins/echarts';

import {
  ElAlert,
  ElButton,
  ElCard,
  ElCheckbox,
  ElDescriptions,
  ElDescriptionsItem,
  ElMessage,
  ElRadioButton,
  ElRadioGroup,
  ElSkeleton,
  ElTable,
  ElTableColumn,
  ElTag,
  ElTooltip,
} from 'element-plus';

import { getProductAnalyticsDetailApi } from '#/api/tiktok-operations';

import { applicableProductColumns, metricHelpText } from './analytics-metrics';
import AnalyticsProductImage from './AnalyticsProductImage.vue';
import MetricHelp from './MetricHelp.vue';
import ProductTrafficFunnel from './ProductTrafficFunnel.vue';

const route = useRoute();
const router = useRouter();
const loading = ref(false);
const preparing = ref(false);
const preparingProgress = ref(0);
const response = ref<Record<string, any>>({});
const channel = ref<ChannelKey>((route.query.channel as ChannelKey) || 'ALL');
const selectedTrendMetrics = ref([
  'gmv',
  'orders',
  'skuOrders',
  'itemsSold',
  'impressions',
]);
const chartRef = ref<EchartsUIType>();
const { renderEcharts } = useEcharts(chartRef);
let detailRequestRunning = false;
let preparingProgressTimer: ReturnType<typeof setInterval> | undefined;
let preparingRetryTimer: ReturnType<typeof setTimeout> | undefined;
let preparingRetryAttempt = 0;

const product = computed(() => response.value.product || {});
const detail = computed(() => product.value.detail || {});
const currentMetrics = computed(() => {
  const metrics = product.value.channelMetrics?.[channel.value];
  return channel.value === 'ALL'
    ? { ...product.value, ...metrics }
    : {
        ...metrics,
        ...(channel.value === 'SELLER_LIVE'
          ? { sellerLiveGmv: metrics?.gmv }
          : {}),
        ...(channel.value === 'SELLER_VIDEO'
          ? { sellerVideoGmv: metrics?.gmv }
          : {}),
        ...(channel.value === 'SELLER_PRODUCT_CARD'
          ? { sellerCardGmv: metrics?.gmv }
          : {}),
        ...(channel.value === 'AFFILIATE'
          ? {
              affiliateGmv: metrics?.gmv,
              affiliateVideoGmv: product.value.affiliateVideoGmv,
              creatorLiveGmv: product.value.creatorLiveGmv,
            }
          : {}),
      };
});
const metricCards = computed(() =>
  applicableProductColumns('PRODUCT', channel.value),
);
const trendRows = computed<Record<string, any>[]>(
  () => response.value.trend || [],
);
const skuRows = computed(() => {
  const skus = response.value.skus || {};
  return (
    skus.rows ||
    skus.skus ||
    skus.shop_skus ||
    skus.performance ||
    skus.data ||
    []
  );
});
const visibleWarnings = computed(() =>
  (response.value.warnings || []).filter((warning: string) =>
    warning.includes('授权'),
  ),
);

function firstValue(source: Record<string, any>, paths: string[]) {
  for (const path of paths) {
    let value: any = source;
    for (const key of path.split('.')) value = value?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

function numberValue(value: any) {
  const raw =
    typeof value === 'object' ? (value?.amount ?? value?.value) : value;
  const result = Number(
    String(raw ?? 0)
      .replaceAll(',', '')
      .replace('%', ''),
  );
  return Number.isFinite(result) ? result : 0;
}

function metricDisplay(card: Record<string, any>) {
  const value = currentMetrics.value[card.key];
  if (value === undefined || value === null || value === '') return '--';
  if (card.percentage) {
    const number = numberValue(value);
    return `${number <= 1 ? (number * 100).toFixed(2) : number.toFixed(2)}%`;
  }
  if (card.money)
    return `${numberValue(value).toLocaleString()} ${currentMetrics.value.currency || product.value.currency || ''}`.trim();
  return numberValue(value).toLocaleString();
}

function trendMetricValue(row: Record<string, any>, key: string) {
  return row.channelMetrics?.[channel.value]?.[key];
}

function trendMetricAvailable(metric: ProductColumnDefinition) {
  if (loading.value && trendRows.value.length === 0) return true;
  return trendRows.value.some((row) => {
    const value = trendMetricValue(row, metric.key);
    return value !== undefined && value !== null && value !== '';
  });
}

function toggleTrendMetric(metric: ProductColumnDefinition) {
  const selected = selectedTrendMetrics.value;
  if (selected.includes(metric.key)) {
    selectedTrendMetrics.value = selected.filter((key) => key !== metric.key);
    return;
  }
  if (!trendMetricAvailable(metric)) {
    ElMessage.info('TikTok 当前接口未返回该指标的趋势数据');
    return;
  }
  if (selected.length >= 5) {
    ElMessage.warning('趋势明细每次最多选择 5 个指标');
    return;
  }
  selectedTrendMetrics.value = [...selected, metric.key];
}

function normalizeTrendMetricSelection() {
  const available = metricCards.value.filter((metric) =>
    trendMetricAvailable(metric),
  );
  const allowed = new Set(available.map((metric) => metric.key));
  const selected = selectedTrendMetrics.value.filter((key) => allowed.has(key));
  selectedTrendMetrics.value = (
    selected.length > 0
      ? selected
      : available.slice(0, 5).map((metric) => metric.key)
  ).slice(0, 5);
}

function metricUnit(metric: ProductColumnDefinition) {
  if (metric.percentage) return 'percent';
  if (metric.money) return 'money';
  return 'count';
}

function metricAxisName(unit: string) {
  if (unit === 'money') return '金额';
  if (unit === 'percent') return '百分比';
  return '数量';
}

async function renderTrendChart() {
  await nextTick();
  const selected = metricCards.value.filter(
    (metric) =>
      selectedTrendMetrics.value.includes(metric.key) &&
      trendMetricAvailable(metric),
  );
  const units = [...new Set(selected.map((metric) => metricUnit(metric)))];
  if (units.length === 0) units.push('count');
  await renderEcharts({
    grid: {
      bottom: 30,
      containLabel: true,
      left: 24,
      right: units.length > 2 ? 92 : 32,
      top: 58,
    },
    legend: {
      data: selected.map((metric) => metric.label),
      top: 0,
      type: 'scroll',
    },
    series: selected.map((metric) => ({
      data: trendRows.value.map((row) =>
        numberValue(trendMetricValue(row, metric.key)),
      ),
      name: metric.label,
      showSymbol: trendRows.value.length < 15,
      smooth: true,
      type: 'line',
      yAxisIndex: units.indexOf(metricUnit(metric)),
    })),
    tooltip: { trigger: 'axis' },
    xAxis: {
      data: trendRows.value.map((row) => row.date || '-'),
      type: 'category',
    },
    yAxis: units.map((unit, index) => ({
      axisLabel: { formatter: unit === 'percent' ? '{value}%' : '{value}' },
      name: metricAxisName(unit),
      offset: index > 1 ? (index - 1) * 60 : 0,
      position: index === 0 ? 'left' : 'right',
      type: 'value',
    })),
  });
}

function intervalValue(row: Record<string, any>, paths: string[]) {
  return firstValue(row, paths) ?? 0;
}

function clearPreparing() {
  if (preparingRetryTimer) clearTimeout(preparingRetryTimer);
  if (preparingProgressTimer) clearInterval(preparingProgressTimer);
  preparingRetryTimer = undefined;
  preparingProgressTimer = undefined;
  preparingRetryAttempt = 0;
  preparing.value = false;
  preparingProgress.value = 100;
}

function schedulePreparingRetry() {
  preparing.value = true;
  if (!preparingProgressTimer) {
    preparingProgress.value = Math.max(8, preparingProgress.value);
    preparingProgressTimer = setInterval(() => {
      preparingProgress.value = Math.min(
        92,
        preparingProgress.value +
          Math.max(1, Math.round((92 - preparingProgress.value) / 10)),
      );
    }, 600);
  }
  if (preparingRetryTimer) return;
  const delay = preparingRetryAttempt < 10 ? 3000 : 10_000;
  preparingRetryTimer = setTimeout(() => {
    preparingRetryTimer = undefined;
    preparingRetryAttempt += 1;
    void loadDetail(true);
  }, delay);
}

async function loadDetail(retry = false) {
  if (detailRequestRunning) return;
  detailRequestRunning = true;
  if (!retry) {
    clearPreparing();
    loading.value = true;
  }
  try {
    const result = await getProductAnalyticsDetailApi({
      channel: 'ALL',
      currency: 'LOCAL',
      endDateLt: route.query.endDateLt,
      productId: route.params.productId,
      productLevel: 'PRODUCT',
      shopId: Number(route.query.shopId),
      startDateGe: route.query.startDateGe,
      statuses: String(route.query.statuses || 'ACTIVATE').split(','),
    });
    if (result.dataStatus === 'PREPARING') {
      if (
        Object.keys(response.value).length === 0 ||
        response.value.dataStatus === 'PREPARING'
      ) {
        response.value = result;
      }
      schedulePreparingRetry();
    } else {
      response.value = result;
      clearPreparing();
    }
  } finally {
    detailRequestRunning = false;
    if (!retry) loading.value = false;
  }
  normalizeTrendMetricSelection();
}

onMounted(loadDetail);
onBeforeUnmount(clearPreparing);
watch(channel, () => {
  normalizeTrendMetricSelection();
  void renderTrendChart();
});
watch([selectedTrendMetrics, trendRows], renderTrendChart, {
  deep: true,
});
</script>

<template>
  <div class="detail-page">
    <div class="page-heading">
      <div>
        <ElButton link type="primary" @click="router.back()">
          ← 返回商品分析
        </ElButton>
        <h2>商品分析详细信息</h2>
        <p>
          数据时区：{{ response.timezone || 'GMT-08:00' }} · 最新可用日期：{{
            response.latestAvailableDate || '-'
          }}
        </p>
      </div>
      <ElButton :loading="loading" @click="loadDetail(false)">刷新</ElButton>
    </div>

    <div v-if="preparing" class="detail-loading-strip">
      <div>
        <strong>详细数据正在准备中</strong>
        <span>商品资料已可查看，指标完成后将自动更新</span>
      </div>
      <ElProgress
        :percentage="preparingProgress"
        :show-text="false"
        :stroke-width="5"
      />
    </div>

    <ElAlert
      v-for="warning in visibleWarnings"
      :key="warning"
      class="warning"
      type="warning"
      :closable="false"
      :title="warning"
    />

    <ElSkeleton
      :loading="loading && Object.keys(response).length === 0"
      animated
      :rows="8"
    >
      <ElCard shadow="never" class="product-summary">
        <AnalyticsProductImage :product="product" class="product-image" />
        <div class="product-copy">
          <ElTooltip
            :content="product.title || detail.title || '-'"
            placement="top"
          >
            <h3 class="product-title">
              {{ product.title || detail.title || '-' }}
            </h3>
          </ElTooltip>
          <ElTag type="success">
            {{ product.statusLabel || product.status || '-' }}
          </ElTag>
          <ElDescriptions :column="2" border class="descriptions">
            <ElDescriptionsItem label="商品 ID">
              {{ product.productId || route.params.productId }}
            </ElDescriptionsItem>
            <ElDescriptionsItem label="类目">
              {{
                firstValue(detail, [
                  'category_chains.0.local_name',
                  'category.name',
                  'category_name',
                ]) || '-'
              }}
            </ElDescriptionsItem>
            <ElDescriptionsItem label="价格">
              {{
                firstValue(detail, [
                  'skus.0.price.sale_price',
                  'price.sale_price',
                  'price',
                ]) || '-'
              }}
            </ElDescriptionsItem>
            <ElDescriptionsItem label="评分">
              {{
                firstValue(detail, [
                  'rating.average_rating',
                  'rating',
                  'review_rating',
                ]) || '-'
              }}
            </ElDescriptionsItem>
          </ElDescriptions>
        </div>
      </ElCard>

      <ElCard shadow="never" class="section-card">
        <div class="section-heading">
          <div>
            <h3>关键指标</h3>
            <p>切换渠道时只改变本页统计口径，不重复拉取全量商品。</p>
          </div>
          <ElRadioGroup v-model="channel">
            <ElRadioButton value="ALL">全部</ElRadioButton>
            <ElRadioButton value="SELLER_LIVE">商家直播</ElRadioButton>
            <ElRadioButton value="SELLER_VIDEO">商家视频</ElRadioButton>
            <ElRadioButton value="SELLER_PRODUCT_CARD">
              商家商品卡
            </ElRadioButton>
            <ElRadioButton value="AFFILIATE">联盟</ElRadioButton>
          </ElRadioGroup>
        </div>
        <div class="metric-grid">
          <div
            v-for="card in metricCards"
            :key="card.key"
            class="metric-card"
            :class="{
              selected: selectedTrendMetrics.includes(card.key),
              unavailable: !trendMetricAvailable(card),
            }"
            @click="toggleTrendMetric(card)"
          >
            <div class="metric-card-label">
              <span>
                {{ card.label }}
                <MetricHelp
                  :content="metricHelpText(card)"
                  :label="card.label"
                />
              </span>
              <ElCheckbox
                :model-value="selectedTrendMetrics.includes(card.key)"
                :disabled="!trendMetricAvailable(card)"
                @click.stop
                @change="toggleTrendMetric(card)"
              />
            </div>
            <strong>{{ metricDisplay(card) }}</strong>
          </div>
        </div>
      </ElCard>

      <ElCard shadow="never" class="section-card">
        <h3>商品流量漏斗</h3>
        <ProductTrafficFunnel
          :analytics-available="response.analyticsAvailable !== false"
          :metrics="currentMetrics"
        />
      </ElCard>

      <ElCard shadow="never" class="section-card">
        <div class="section-heading">
          <div>
            <h3>趋势明细</h3>
            <p>点击上方关键指标加入趋势图，每次最多选择 5 个指标。</p>
          </div>
          <span class="trend-count">
            已选择 {{ selectedTrendMetrics.length }}/5
          </span>
        </div>
        <EchartsUI ref="chartRef" height="360px" />
      </ElCard>

      <ElCard shadow="never" class="section-card">
        <h3>SKU 概览</h3>
        <ElTable :data="skuRows" border empty-text="暂无 SKU 分析数据">
          <ElTableColumn prop="skuId" min-width="180">
            <template #header>
              <span class="field-heading">
                SKU ID
                <MetricHelp
                  content="TikTok Shop 为商品销售规格分配的唯一标识。"
                  label="SKU ID"
                />
              </span>
            </template>
          </ElTableColumn>
          <ElTableColumn label="GMV">
            <template #header>
              <span class="field-heading">
                GMV
                <MetricHelp
                  :content="metricHelpText({ key: 'gmv' })"
                  label="GMV"
                />
              </span>
            </template>
            <template #default="{ row }">
              {{
                intervalValue(row, [
                  'gmv',
                  'performance.sales.gmv.amount',
                  'sales.gmv.amount',
                  'gmv.amount',
                ])
              }}
            </template>
          </ElTableColumn>
          <ElTableColumn label="SKU 订单数">
            <template #header>
              <span class="field-heading">
                SKU 订单数
                <MetricHelp
                  :content="metricHelpText({ key: 'skuOrders' })"
                  label="SKU 订单数"
                />
              </span>
            </template>
            <template #default="{ row }">
              {{
                intervalValue(row, [
                  'skuOrders',
                  'performance.sales.sku_orders_count',
                  'sales.sku_orders_count',
                  'sku_orders_count',
                ])
              }}
            </template>
          </ElTableColumn>
          <ElTableColumn label="成交件数">
            <template #header>
              <span class="field-heading">
                成交件数
                <MetricHelp
                  :content="metricHelpText({ key: 'itemsSold' })"
                  label="成交件数"
                />
              </span>
            </template>
            <template #default="{ row }">
              {{
                intervalValue(row, [
                  'itemsSold',
                  'performance.sales.items_sold',
                  'sales.items_sold',
                  'items_sold',
                ])
              }}
            </template>
          </ElTableColumn>
        </ElTable>
      </ElCard>
    </ElSkeleton>
  </div>
</template>

<style scoped>
.detail-page {
  min-height: 100%;
  padding: 18px;
  background: #f5f6f7;
}

.page-heading,
.section-heading,
.product-summary {
  display: flex;
  gap: 18px;
  align-items: flex-start;
  justify-content: space-between;
}

h2,
h3,
p {
  margin: 0;
}

.page-heading {
  margin-bottom: 16px;
}

.page-heading p,
.section-heading p {
  margin-top: 6px;
  color: var(--el-text-color-secondary);
}

.warning {
  margin-bottom: 12px;
}

.detail-loading-strip {
  display: grid;
  gap: 8px;
  padding: 10px 14px;
  margin-bottom: 12px;
  background: var(--el-color-primary-light-9);
  border: 1px solid var(--el-color-primary-light-7);
  border-radius: 8px;
}

.detail-loading-strip > div {
  display: flex;
  gap: 10px;
  align-items: baseline;
}

.detail-loading-strip span {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.product-summary,
.section-card {
  width: 100%;
  margin-bottom: 16px;
  border-radius: 12px;
}

.product-summary :deep(.el-card__body) {
  display: flex;
  gap: 18px;
}

.product-image {
  flex: 0 0 auto;
  width: 128px;
  height: 128px;
  border-radius: 10px;
}

.product-copy {
  flex: 1;
  min-width: 0;
}

.product-copy h3 {
  margin-bottom: 10px;
  font-size: 19px;
}

.product-title {
  display: -webkit-box;
  max-width: min(880px, 72vw);
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.descriptions {
  margin-top: 14px;
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.metric-card {
  display: grid;
  gap: 8px;
  padding: 14px;
  cursor: pointer;
  background: var(--el-fill-color-lighter);
  border: 1px solid transparent;
  border-radius: 9px;
}

.metric-card.selected {
  background: rgb(0 174 167 / 7%);
  border-color: #00aea7;
}

.metric-card.unavailable {
  cursor: not-allowed;
  opacity: 0.58;
}

.metric-card-label,
.metric-card-label span,
.field-heading {
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: space-between;
}

.metric-card-label span,
.field-heading {
  justify-content: flex-start;
}

.metric-card span {
  color: var(--el-text-color-secondary);
}

.metric-card strong {
  font-size: 20px;
}

.trend-count {
  color: var(--el-color-primary);
}

@media (max-width: 1100px) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(150px, 1fr));
  }

  .section-heading {
    flex-direction: column;
  }
}
</style>
